// Calendar / Appointments tab — this lead's upcoming and past appointments plus
// a Create Appointment shortcut (opens ScheduleAppointmentModal, which checks
// Google Calendar free/busy and syncs the booking). Appointments come from the
// tenant-scoped activity-log endpoint.

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarPlus, Calendar, CheckCircle2, AlertTriangle } from 'lucide-react';
import { apiGet } from '../../../lib/api.js';
import { fmtDate } from '../../../lib/format.js';

function ApptList({ title, items }) {
  if (!items.length) return null;
  return (
    <section className="space-y-2">
      <h4 className="text-[10px] uppercase tracking-wider text-white/40">{title}</h4>
      <ul className="space-y-2">
        {items.map((a, i) => (
          <li key={`${a.meta?.appointmentId || i}`} className="flex gap-3 rounded-lg bg-civix-panel border border-civix-border px-3 py-2">
            <Calendar className="w-4 h-4 text-civix-purpleSoft shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold text-white/85 truncate">{a.meta?.apptTitle || a.title}</div>
              <div className="text-[11px] text-white/60">{a.meta?.startAt ? fmtDate(a.meta.startAt) : a.detail}</div>
              <div className="mt-1 flex items-center gap-2 text-[10px]">
                {a.status && <span className="px-1.5 rounded bg-white/10 text-white/60">{a.status}</span>}
                {a.meta?.googleSynced && (
                  <span className="flex items-center gap-1 text-emerald-300"><CheckCircle2 className="w-3 h-3" /> Synced to Google Calendar</span>
                )}
                {a.meta?.googleSyncError && (
                  <span className="flex items-center gap-1 text-amber-300" title={a.meta.googleSyncError}><AlertTriangle className="w-3 h-3" /> Google sync failed</span>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function CalendarTab({ leadId, onCreateAppointment }) {
  const { data } = useQuery({
    queryKey: ['lead-activity-log', leadId],
    queryFn: () => apiGet(`/leads/${leadId}/activity-log`),
    enabled: !!leadId,
  });
  const appts = (data?.events || []).filter((e) => e.type === 'appointment');
  const now = Date.now();
  const when = (a) => (a.meta?.startAt ? new Date(a.meta.startAt).getTime() : 0);
  const upcoming = appts.filter((a) => when(a) >= now).sort((a, b) => when(a) - when(b));
  const past = appts.filter((a) => when(a) < now).sort((a, b) => when(b) - when(a));

  return (
    <div className="space-y-4 p-1 overflow-y-auto h-full">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-white/85">Appointments</h3>
        <button onClick={onCreateAppointment} className="btn-primary text-xs px-3 py-2 flex items-center gap-1.5">
          <CalendarPlus className="w-4 h-4" /> Create appointment
        </button>
      </div>
      {appts.length === 0 && <p className="text-xs text-white/40 p-3">No appointments scheduled.</p>}
      <ApptList title="Upcoming" items={upcoming} />
      <ApptList title="Past" items={past} />
    </div>
  );
}
